import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import { SidebarToggleButton } from '../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import { qPatientsList, type PatientListRow } from '@helios/data-access';
import { downloadCsv, type CsvColumn } from '../../lib/csv-export';

// ─── Types ───

type AccountStatus = 'active' | 'archived';

interface PatientVM {
  id: string;
  initials: string;
  avatarColor: string;
  name: string;
  gender: string;
  age: number | null;
  patientNo: string;
  idType: string;
  idValue: string;
  lastVisit: string;
  status: AccountStatus;
  isFavorite: boolean;
}

// ─── Helpers ───

const AVATAR_COLORS = [
  '#f97316',
  '#3b82f6',
  '#a855f7',
  '#22c55e',
  '#ef4444',
  '#14b8a6',
  '#f59e0b',
  '#6366f1',
  '#ec4899',
  '#0ea5e9',
];

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function computeAge(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const monthDiff = now.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function toPatientVM(row: PatientListRow): PatientVM {
  const name = row.full_name || 'Unknown';
  return {
    id: row.id,
    initials: getInitials(name),
    avatarColor: AVATAR_COLORS[hashCode(row.id) % AVATAR_COLORS.length],
    name,
    gender: row.sex ?? '',
    age: computeAge(row.birth_date),
    patientNo: row.patient_number ?? '',
    idType: row.id_type ?? '',
    idValue: row.id_value ?? '',
    lastVisit: formatDate(row.last_visited_at ?? row.updated_at),
    status: row.status === 'archived' ? 'archived' : 'active',
    isFavorite: row.is_favorite === 1,
  };
}

// ─── Debounce hook ───

function useDebouncedValue(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ─── Status config ───

const STATUS_LABELS: Record<AccountStatus, string> = {
  active: 'Active',
  archived: 'Archived',
};

const STATUS_STYLES: Record<AccountStatus, { bg: string; color: string }> = {
  active: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  archived: { bg: 'var(--color-neutral-bg)', color: 'var(--text-muted)' },
};

const STATUS_OPTIONS: Array<{ value: AccountStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
];

const SORT_OPTIONS: Array<{ value: 'last-visit' | 'name-az'; label: string }> = [
  { value: 'last-visit', label: 'Last visit' },
  { value: 'name-az', label: 'Name A-Z' },
];

// ─── CSV export ───

const CSV_COLUMNS: CsvColumn<PatientVM>[] = [
  { header: 'Name', get: (p) => p.name },
  { header: 'Patient No.', get: (p) => p.patientNo },
  { header: 'Gender', get: (p) => p.gender },
  { header: 'Age', get: (p) => (p.age != null ? String(p.age) : '') },
  { header: 'ID Type', get: (p) => p.idType },
  { header: 'ID No.', get: (p) => p.idValue },
  { header: 'Last Visit', get: (p) => p.lastVisit },
  { header: 'Status', get: (p) => STATUS_LABELS[p.status] },
];

// ─── Icons ───

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IdCardIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  );
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill={filled ? 'var(--color-accent)' : 'none'}
      stroke={filled ? 'var(--color-accent)' : 'var(--text-muted)'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

// ─── StatusPill ───

function StatusPill({ status }: { status: AccountStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '24px',
        letterSpacing: '0.12px',
        whiteSpace: 'nowrap',
      }}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// ─── FilterDropdown ───

function FilterDropdown<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          marginRight: 'var(--sp-1)',
        }}
      >
        {label}:
      </span>
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value as T)}
          style={{
            appearance: 'none',
            background: 'transparent',
            border: 'none',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
            cursor: 'pointer',
            paddingRight: 18,
            fontFamily: 'var(--font-family)',
          }}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span
          style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'var(--text-muted)',
            display: 'flex',
          }}
        >
          <ChevronDownIcon />
        </span>
      </div>
    </div>
  );
}

// ─── PatientRow ───

function PatientRow({
  patient,
  onToggleFavorite,
  onClick,
}: {
  patient: PatientVM;
  onToggleFavorite: () => void;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4) var(--sp-6)',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        transition: 'background 150ms ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 'var(--radius-full)',
          background: patient.avatarColor,
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          flexShrink: 0,
          letterSpacing: '0.5px',
        }}
      >
        {patient.initials}
      </div>

      {/* Name + demographics + patient no */}
      <div style={{ minWidth: 140, flexShrink: 0 }}>
        <div
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-base)',
            color: 'var(--text)',
          }}
        >
          {patient.name}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            lineHeight: 'var(--line-height-sm)',
            color: 'var(--text-muted)',
            marginTop: 2,
          }}
        >
          {[patient.gender, patient.age != null ? `${patient.age}y` : null]
            .filter(Boolean)
            .join(' \u2022 ')}
        </div>
        {patient.patientNo && (
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              lineHeight: 'var(--line-height-sm)',
              color: 'var(--text-muted)',
              marginTop: 1,
            }}
          >
            Patient No. {patient.patientNo}
          </div>
        )}
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* ID type + value */}
      {(patient.idType || patient.idValue) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
            flexShrink: 0,
          }}
        >
          <IdCardIcon />
          <span>
            {patient.idType} {patient.idValue}
          </span>
        </div>
      )}

      {/* Last visit */}
      <div
        style={{
          textAlign: 'right',
          flexShrink: 0,
          minWidth: 90,
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            lineHeight: 'var(--line-height-sm)',
          }}
        >
          Last visit:
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
            lineHeight: 'var(--line-height-sm)',
          }}
        >
          {patient.lastVisit}
        </div>
      </div>

      {/* Status pill */}
      <StatusPill status={patient.status} />

      {/* Favorite star */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite();
        }}
        style={{
          background: 'none',
          border: 'none',
          padding: 'var(--sp-1)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <StarIcon filled={patient.isFavorite} />
      </button>
    </div>
  );
}

// ─── Main Page ───

export function AllPatientsPage() {
  const { collapsed = false, onToggleSidebar } =
    useOutletContext<{ collapsed: boolean; onToggleSidebar?: () => void }>() ?? {};
  const navigate = useNavigate();
  const { user } = useAuth();
  const { db } = usePowerSyncDb();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<AccountStatus | 'all'>('all');
  const [sortBy, setSortBy] = useState<'last-visit' | 'name-az'>('last-visit');

  const debouncedSearch = useDebouncedValue(search, 300);

  // Build query from current filters
  const spec = useMemo(
    () =>
      qPatientsList({
        userId: user?.uid,
        search: debouncedSearch || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sort: sortBy,
      }),
    [user?.uid, debouncedSearch, statusFilter, sortBy],
  );

  const { data: rows = [] } = usePowerSyncQuery<PatientListRow>(spec);

  // Transform DB rows → display view models
  const patients = useMemo(() => rows.map(toPatientVM), [rows]);

  async function toggleFavorite(patientId: string, currentlyFavorite: boolean) {
    if (!db || !user?.uid) return;
    if (currentlyFavorite) {
      await db.execute(`DELETE FROM user_patient_favorites WHERE user_id = ? AND patient_id = ?`, [
        user.uid,
        patientId,
      ]);
    } else {
      await db.execute(
        `INSERT INTO user_patient_favorites (id, user_id, patient_id) VALUES (?, ?, ?)`,
        [crypto.randomUUID(), user.uid, patientId],
      );
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '20px var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flexShrink: 0,
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {onToggleSidebar && (
            <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
          )}
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-xl)',
              letterSpacing: '-0.44px',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            All Patients
          </h1>
        </div>
      </div>

      {/* Search + Filters bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--sp-4) var(--sp-6)',
          flexShrink: 0,
        }}
      >
        {/* Search input */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            height: 'var(--input-height)',
            padding: '0 var(--input-padding-x)',
            border: '1px solid var(--border-input)',
            borderRadius: 'var(--input-radius)',
            background: 'var(--input-bg)',
            width: 320,
            maxWidth: '100%',
          }}
        >
          <span style={{ color: 'var(--text-muted)', display: 'flex', flexShrink: 0 }}>
            <SearchIcon />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search patient name, Patient No., ID No."
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontSize: 'var(--font-size-base)',
              color: 'var(--input-text)',
              fontFamily: 'var(--font-family)',
              outline: 'none',
              minWidth: 0,
            }}
          />
        </div>

        {/* Status + Sort dropdowns */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-6)' }}>
          <FilterDropdown
            label="Status"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
          />
          <FilterDropdown label="Sort" value={sortBy} options={SORT_OPTIONS} onChange={setSortBy} />
          <button
            type="button"
            onClick={() => downloadCsv(patients, CSV_COLUMNS, 'patients')}
            disabled={patients.length === 0}
            title="Export current view to CSV"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              height: 'var(--input-height)',
              padding: '0 var(--sp-3)',
              border: '1px solid var(--border-input)',
              borderRadius: 'var(--input-radius)',
              background: 'var(--bg-surface)',
              color: patients.length === 0 ? 'var(--text-muted)' : 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              fontFamily: 'var(--font-family)',
              cursor: patients.length === 0 ? 'not-allowed' : 'pointer',
              flexShrink: 0,
            }}
          >
            <DownloadIcon />
            Export CSV
          </button>
        </div>
      </div>

      {/* Patient list */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0 var(--sp-6) var(--sp-6)',
        }}
      >
        <div
          style={{
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border)',
            overflow: 'hidden',
          }}
        >
          {patients.length === 0 ? (
            <div
              style={{
                padding: 'var(--sp-12) var(--sp-6)',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-base)',
              }}
            >
              No patients match the current filters.
            </div>
          ) : (
            patients.map((patient) => (
              <PatientRow
                key={patient.id}
                patient={patient}
                onToggleFavorite={() => toggleFavorite(patient.id, patient.isFavorite)}
                onClick={() => navigate(`/patient/${patient.id}`)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
